import { API_BASE, VAPID_PUBLIC_KEY } from './config';
import { registerServiceWorker } from './serviceWorkerRegistration';

export function isPushSupported() {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function getPermission() {
    return isPushSupported() ? Notification.permission : 'unsupported';
}

// urlBase64 -> Uint8Array, as required by pushManager.subscribe's applicationServerKey.
function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
}

// Called only from a direct user gesture (the bell toggle click) — turns the
// personal preference ON: requests browser permission if needed, subscribes
// to push, and tells the backend so events are actually sent to this user.
// Returns { success: true } or { success: false, reason: 'denied' | 'unsupported' | 'error' }.
export async function enableNotifications(username) {
    if (!isPushSupported()) return { success: false, reason: 'unsupported' };

    let permission = Notification.permission;
    if (permission === 'default') {
        permission = await Notification.requestPermission();
    }
    if (permission !== 'granted') return { success: false, reason: 'denied' };

    try {
        const registration = await registerServiceWorker();
        if (!registration) return { success: false, reason: 'error' };

        let subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
            subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
            });
        }

        const res = await fetch(`${API_BASE}/push-subscriptions?action=subscribe`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, subscription, user_agent: navigator.userAgent }),
        });
        if (!res.ok) return { success: false, reason: 'error' };

        return { success: true };
    } catch (e) {
        return { success: false, reason: 'error' };
    }
}

// Called on every portal load when the user is supposed to have notifications
// on (per the persisted preference) — NOT from a user gesture like
// enableNotifications() is. Silently re-establishes the browser's push
// subscription if it's gone missing (cleared site data, browser profile
// reset, the push service dropping the registration) without prompting for
// permission again — Notification.requestPermission() only works from a user
// gesture anyway, so this only ever acts when permission is already
// 'granted'. This is what stops "toggle says on, nothing arrives" from
// persisting silently forever: without it, a dead local subscription is
// invisible until the next send attempt 404s/410s server-side and prunes the
// DB row — which never re-creates a working one on its own.
export async function ensureSubscription(username) {
    if (!isPushSupported()) return;
    if (Notification.permission !== 'granted') return;

    try {
        const registration = await registerServiceWorker();
        if (!registration) return;

        let subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
            subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
            });
            await fetch(`${API_BASE}/push-subscriptions?action=subscribe`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, subscription, user_agent: navigator.userAgent }),
            }).catch(() => {});
        }
    } catch (e) { /* best-effort — next portal load tries again */ }
}

// Turns the personal preference OFF — unsubscribes this browser and tells
// the backend to stop sending, regardless of why (explicit toggle-off, or
// permission having been revoked at the OS/browser level meanwhile).
export async function disableNotifications(username) {
    try {
        if ('serviceWorker' in navigator) {
            const registration = await navigator.serviceWorker.getRegistration();
            const subscription = registration && await registration.pushManager.getSubscription();
            if (subscription) {
                await fetch(`${API_BASE}/push-subscriptions?action=unsubscribe`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ username, endpoint: subscription.endpoint }),
                }).catch(() => {});
                await subscription.unsubscribe();
                return;
            }
        }
        // No local subscription found (different device/browser than the one
        // that enabled it) — still tell the backend to turn the flag off.
        await fetch(`${API_BASE}/push-subscriptions?action=unsubscribe`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username }),
        }).catch(() => {});
    } catch (e) { /* best-effort */ }
}
